'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { useMountEffect } from '@/hooks/useMountEffect';
import { MISSION_TEMPLATES, patternPoints, pickTemplate, polyline, type MissionTemplate, type MissionTemplateName, type Point } from '@/lib/skyscout/missions';
export interface CommandToMissionProps {
  className?: string;
  /** Example commands offered as chips; the first one plays when the demo scrolls into view. */
  examples?: string[];
  /** Rendered under the demo, inside the figure. */
  children?: React.ReactNode;
}
const DEFAULT_EXAMPLES = ['inspect the roof', 'search the field for a blue jacket', 'patrol the fence line', 'survey the crops', 'count the cars in the lot', 'deliver this to the shed'];
const TYPE_MS = 22;
const JSON_MS = 520;
const FENCE = 105;
interface Token {
  text: string;
  kind?: 'k' | 'v';
}
function missionTokens(command: string, t: MissionTemplate): Token[] {
  const out: Token[] = [];
  const raw = (text: string) => out.push({
    text
  });
  const key = (k: string) => out.push({
    text: `"${k}"`,
    kind: 'k'
  });
  const val = (v: string | number | boolean) => out.push({
    text: typeof v === 'string' ? `"${v}"` : String(v),
    kind: 'v'
  });
  const line = (indent: string, k: string, v: string | number | boolean, comma = true) => {
    raw(indent);
    key(k);
    raw(': ');
    val(v);
    raw(comma ? ',\n' : '\n');
  };
  const open = (indent: string, k: string) => {
    raw(indent);
    key(k);
    raw(': {\n');
  };
  raw('{\n');
  line('  ', 'command', command);
  line('  ', 'mission_type', t.mission_type);
  line('  ', 'target_description', t.target_description);
  line('  ', 'flight_pattern', t.flight_pattern);
  open('  ', 'parameters');
  line('    ', 'altitude', t.parameters.altitude);
  line('    ', 'speed', t.parameters.speed);
  line('    ', 'radius', t.parameters.radius, false);
  raw('  },\n');
  open('  ', 'safety');
  open('    ', 'geofence');
  line('      ', 'max_altitude', t.safety.geofence.max_altitude);
  line('      ', 'max_distance', t.safety.geofence.max_distance, false);
  raw('    },\n');
  line('    ', 'rtl_battery_threshold', t.safety.rtl_battery_threshold);
  line('    ', 'weather_check', t.safety.weather_check);
  line('    ', 'max_wind_speed', t.safety.max_wind_speed, false);
  raw('  }\n}');
  return out;
}
function renderTokens(tokens: Token[], limit: number) {
  let left = limit;
  let offset = 0;
  const nodes: React.ReactNode[] = [];
  for (const tok of tokens) {
    if (left <= 0) break;
    const text = tok.text.slice(0, left);
    const start = offset;
    offset += tok.text.length;
    left -= text.length;
    nodes.push(tok.kind ? <span key={start} className={tok.kind === 'k' ? 'text-zinc-900 dark:text-zinc-100' : 'text-[var(--sky)]'}>
          {text}
        </span> : text);
  }
  return nodes;
}
function flightFor(name: MissionTemplateName) {
  const t = MISSION_TEMPLATES[name];
  // The geofence fills the map; the coverage area sits inside it.
  const scale = FENCE / t.safety.geofence.max_distance;
  const r = Math.max(t.parameters.radius * scale, 10);
  // Rounded so the server and the browser agree on every derived number;
  // Math.cos is allowed to differ in the last bit between engines.
  const points = patternPoints(t.flight_pattern, r).map(([x, y]): Point => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
  const line = polyline(points);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  return {
    r,
    points,
    d,
    line
  };
}
const GRID = Array.from({
  length: 13
}, (_, i) => -120 + i * 20);
interface Plan {
  id: number;
  command: string;
  name: MissionTemplateName;
  matched: boolean;
}

/**
 * Type an instruction (or pick an example) and watch it become the mission
 * JSON the agent emits, then see the drone fly that template's pattern inside
 * its geofence. Template choice is a keyword stand-in for the LLM; every
 * number comes from the real mission templates.
 */
export function CommandToMission(t0) {
  const $ = _c(59);
  const {
    className: t1,
    examples: t2,
    children
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const examples = t2 === undefined ? DEFAULT_EXAMPLES : t2;
  const first = examples[0] ?? "inspect the roof";
  const figureRef = useRef(null);
  const flownRef = useRef(null);
  const dotRef = useRef(null);
  const rafRef = useRef(0);
  const [input, setInput] = useState(first);
  let t3;
  if ($[0] !== first) {
    t3 = () => ({
      id: 0,
      command: first,
      ...pickTemplate(first)
    });
    $[0] = first;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [plan, setPlan] = useState(t3);
  const [jsonChars, setJsonChars] = useState(Number.POSITIVE_INFINITY);
  const template = MISSION_TEMPLATES[plan.name];
  const tokens = missionTokens(plan.command, template);
  let t4;
  if ($[2] !== plan.name) {
    t4 = flightFor(plan.name);
    $[2] = plan.name;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const flight = t4;
  let t5;
  if ($[4] !== flight.points[0]) {
    t5 = flight.points[0] ?? [0, 0];
    $[4] = flight.points[0];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const start = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function run(command, typeCommand) {
      cancelAnimationFrame(rafRef.current);
      const picked = pickTemplate(command);
      const next = flightFor(picked.name);
      const total = missionTokens(command, MISSION_TEMPLATES[picked.name]).reduce(_temp, 0);
      const typeEnd = typeCommand ? command.length * TYPE_MS : 0;
      const flyStart = typeEnd + JSON_MS;
      const flyMs = 900 + next.line.length * 9;
      const begin = performance.now();
      let planned = false;
      if (typeCommand) {
        setInput("");
      }
      const step = now => {
        const elapsed = now - begin;
        if (typeCommand) {
          setInput(command.slice(0, Math.floor(elapsed / TYPE_MS) + 1));
        }
        if (elapsed >= typeEnd) {
          if (!planned) {
            planned = true;
            setPlan(p => ({
              id: p.id + 1,
              command,
              ...picked
            }));
          }
          setJsonChars(elapsed >= flyStart ? Number.POSITIVE_INFINITY : Math.floor((elapsed - typeEnd) / JSON_MS * total));
        }
        if (elapsed >= flyStart) {
          const k = Math.min(1, (elapsed - flyStart) / flyMs);
          const [x, y] = next.line.at(next.line.length * k);
          flownRef.current?.setAttribute("stroke-dashoffset", String(next.line.length * (1 - k)));
          dotRef.current?.setAttribute("cx", String(x));
          dotRef.current?.setAttribute("cy", String(y));
          if (k >= 1) {
            return;
          }
        }
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const run = t6;
  let t7;
  if ($[7] !== first) {
    t7 = () => {
      const figure = figureRef.current;
      if (!figure) {
        return;
      }
      const io = new IntersectionObserver(t8 => {
        const [entry] = t8;
        if (!entry?.isIntersecting) {
          return;
        }
        io.disconnect();
        run(first, true);
      }, {
        threshold: 0.35
      });
      io.observe(figure);
      return () => {
        io.disconnect();
        cancelAnimationFrame(rafRef.current);
      };
    };
    $[7] = first;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useMountEffect(t7);
  const t8 = "Type a command and see the mission plan and the flight pattern it becomes.";
  const t9 = `rounded-2xl border border-zinc-200 bg-white p-4 [--fence:#b4441b] [--sky:#0588d6] sm:p-5 dark:border-zinc-800 dark:bg-zinc-900 dark:[--fence:#f08a5d] dark:[--sky:#4fb6f2] ${className}`;
  let t10;
  if ($[9] !== first || $[10] !== input) {
    t10 = e => {
      e.preventDefault();
      run(input.trim() || first, false);
    };
    $[9] = first;
    $[10] = input;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => setInput(e_0.target.value);
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== input) {
    t12 = <input value={input} onChange={t11} aria-label="Command" className="min-w-0 flex-1 bg-transparent text-[15px] text-zinc-900 outline-none dark:text-zinc-100" />;
    $[13] = input;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <button type="submit" className="rounded-lg px-2.5 py-1.5 font-mono text-xs text-zinc-500 transition-colors hover:bg-zinc-200/70 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100">⏎ plan</button>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t10 || $[17] !== t12) {
    t14 = <form className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 py-1.5 pr-1.5 pl-3 focus-within:border-zinc-400 dark:border-zinc-800 dark:bg-zinc-950 dark:focus-within:border-zinc-600" onSubmit={t10}>{t12}{t13}</form>;
    $[16] = t10;
    $[17] = t12;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== examples || $[20] !== plan.command) {
    let t16;
    if ($[22] !== plan.command) {
      t16 = c => <button key={c} type="button" aria-pressed={plan.command === c} onClick={() => run(c, true)} className="rounded-full border border-zinc-200 px-3 py-1.5 text-[12.5px] leading-none text-zinc-500 transition-colors hover:border-zinc-900 hover:text-zinc-900 aria-pressed:border-zinc-900 aria-pressed:text-zinc-900 dark:border-zinc-800 dark:text-zinc-400 dark:hover:border-zinc-100 dark:hover:text-zinc-100 dark:aria-pressed:border-zinc-100 dark:aria-pressed:text-zinc-100">{c}</button>;
      $[22] = plan.command;
      $[23] = t16;
    } else {
      t16 = $[23];
    }
    t15 = examples.map(t16);
    $[19] = examples;
    $[20] = plan.command;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[24] !== t15) {
    t16 = <div className="mt-3 flex flex-wrap gap-1.5">{t15}</div>;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = plan.matched ? `${template.mission_type}, flown as a ${template.flight_pattern} at ${template.parameters.altitude} m` : "no match, so it's a search. that's the default in the real code too";
  let t18;
  if ($[26] !== t17) {
    t18 = <p aria-live="polite" className="mt-2 min-h-5 text-[13px] text-zinc-500 dark:text-zinc-400">{t17}</p>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = "mt-3 grid gap-4 md:grid-cols-2";
  const t20 = "true";
  const t21 = "m-0 min-h-[300px] overflow-auto rounded-xl border border-zinc-200 bg-zinc-50 p-3.5 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-zinc-500 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400";
  const t22 = renderTokens(tokens, jsonChars);
  let t23;
  if ($[28] !== t22) {
    t23 = <pre aria-hidden={t20} className={t21}>{t22}</pre>;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = GRID.map(_temp2);
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== flight.r) {
    t25 = <circle r={flight.r} className="fill-[var(--sky)] opacity-[0.08]" />;
    $[31] = flight.r;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <circle r={FENCE} fill="none" stroke="var(--fence)" strokeDasharray="3 5" strokeWidth={1.2} opacity={0.7} />;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== flight.d) {
    t27 = <path d={flight.d} fill="none" className="stroke-zinc-400 dark:stroke-zinc-600" strokeWidth={1.2} strokeDasharray="2 4" />;
    $[34] = flight.d;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== flight.d || $[37] !== flight.line.length || $[38] !== plan.id) {
    t28 = <path key={plan.id} ref={flownRef} d={flight.d} fill="none" stroke="var(--sky)" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={flight.line.length} strokeDashoffset={flight.line.length} />;
    $[36] = flight.d;
    $[37] = flight.line.length;
    $[38] = plan.id;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  const t29 = `dot-${plan.id}`;
  let t30;
  if ($[40] !== start[0] || $[41] !== start[1] || $[42] !== t29) {
    t30 = <circle key={t29} ref={dotRef} r={4} cx={start[0]} cy={start[1]} fill="var(--sky)" />;
    $[40] = start[0];
    $[41] = start[1];
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t25 || $[45] !== t27 || $[46] !== t28 || $[47] !== t30) {
    t31 = <svg viewBox="-120 -120 240 240" aria-hidden="true" className="block aspect-square w-full self-start rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950">{t24}{t25}{t26}{t27}{t28}{t30}</svg>;
    $[44] = t25;
    $[45] = t27;
    $[46] = t28;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t23 || $[50] !== t31) {
    t32 = <div className={t19}>{t23}{t31}</div>;
    $[49] = t23;
    $[50] = t31;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== children || $[53] !== t14 || $[54] !== t16 || $[55] !== t18 || $[56] !== t32 || $[57] !== t9) {
    t33 = <figure ref={figureRef} aria-label={t8} className={t9}>{t14}{t16}{t18}{t32}{children}</figure>;
    $[52] = children;
    $[53] = t14;
    $[54] = t16;
    $[55] = t18;
    $[56] = t32;
    $[57] = t9;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  return t33;
}
function _temp2(i) {
  return <path key={i} d={`M${i} -120V120M-120 ${i}H120`} className="stroke-zinc-200 dark:stroke-zinc-800" strokeWidth={0.6} />;
}
function _temp(n, t) {
  return n + t.text.length;
}